import React from 'react';
import { Stage, Layer, Rect, Text as KonvaText, Image as KonvaImage, Circle, Ellipse, RegularPolygon, Star, Arrow, Line as KonvaLine, Shape, Ring, Group } from 'react-konva';
import useImage from 'use-image';
import { getMediaUrl } from '../utils/mediaUrl';

const drawMaskPath = (ctx, maskShape, w, h) => {
  const cx = w / 2, cy = h / 2;
  ctx.beginPath();
  switch (maskShape) {
    case 'rect': ctx.rect(0, 0, w, h); break;
    case 'circle': ctx.arc(cx, cy, Math.min(w, h) / 2, 0, Math.PI * 2); break;
    case 'ellipse': ctx.ellipse(cx, cy, w / 2, h / 2, 0, 0, Math.PI * 2); break;
    case 'triangle': {
      const r = Math.min(w, h) / 2;
      for (let i = 0; i < 3; i++) { const a = (i * 2 * Math.PI / 3) - Math.PI / 2; i === 0 ? ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a)) : ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a)); }
      ctx.closePath(); break;
    }
    case 'diamond': ctx.moveTo(cx, 0); ctx.lineTo(w, cy); ctx.lineTo(cx, h); ctx.lineTo(0, cy); ctx.closePath(); break;
    case 'pentagon': case 'hexagon': case 'octagon': {
      const sides = { pentagon: 5, hexagon: 6, octagon: 8 }[maskShape];
      const rp = Math.min(w, h) / 2;
      for (let i = 0; i < sides; i++) { const a = (i * 2 * Math.PI / sides) - Math.PI / 2; i === 0 ? ctx.moveTo(cx + rp * Math.cos(a), cy + rp * Math.sin(a)) : ctx.lineTo(cx + rp * Math.cos(a), cy + rp * Math.sin(a)); }
      ctx.closePath(); break;
    }
    case 'star': {
      const outerR = Math.min(w, h) / 2, innerR = outerR * 0.4;
      for (let i = 0; i < 10; i++) { const r = i % 2 === 0 ? outerR : innerR; const a = (i * Math.PI / 5) - Math.PI / 2; i === 0 ? ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a)) : ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a)); }
      ctx.closePath(); break;
    }
    case 'star6': {
      const outerR = Math.min(w, h) / 2, innerR = outerR * 0.5;
      for (let i = 0; i < 12; i++) { const r = i % 2 === 0 ? outerR : innerR; const a = (i * Math.PI / 6) - Math.PI / 2; i === 0 ? ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a)) : ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a)); }
      ctx.closePath(); break;
    }
    case 'heart': ctx.moveTo(cx, h * 0.4); ctx.bezierCurveTo(w * 0.48, h * 0.08, 0, h * 0.08, 0, h * 0.4); ctx.bezierCurveTo(0, h * 0.68, cx, h * 0.88, cx, h); ctx.bezierCurveTo(cx, h * 0.88, w, h * 0.68, w, h * 0.4); ctx.bezierCurveTo(w, h * 0.08, w * 0.52, h * 0.08, cx, h * 0.4); ctx.closePath(); break;
    case 'cross': { const arm = w * 0.28; ctx.moveTo(arm, 0); ctx.lineTo(w-arm, 0); ctx.lineTo(w-arm, arm); ctx.lineTo(w, arm); ctx.lineTo(w, h-arm); ctx.lineTo(w-arm, h-arm); ctx.lineTo(w-arm, h); ctx.lineTo(arm, h); ctx.lineTo(arm, h-arm); ctx.lineTo(0, h-arm); ctx.lineTo(0, arm); ctx.lineTo(arm, arm); ctx.closePath(); break; }
    case 'cloud': ctx.arc(w*0.22, h*0.58, h*0.32, Math.PI*0.5, Math.PI*1.5, false); ctx.arc(w*0.45, h*0.3, h*0.26, Math.PI*1.1, Math.PI*1.95, false); ctx.arc(w*0.7, h*0.25, h*0.22, Math.PI*1.6, Math.PI*1.98, false); ctx.arc(w*0.8, h*0.55, h*0.28, Math.PI*1.55, Math.PI*0.5, false); ctx.closePath(); break;
    case 'rounded-rect': { const r = Math.min(w, h) * 0.15; ctx.moveTo(r, 0); ctx.lineTo(w-r, 0); ctx.arcTo(w, 0, w, r, r); ctx.lineTo(w, h-r); ctx.arcTo(w, h, w-r, h, r); ctx.lineTo(r, h); ctx.arcTo(0, h, 0, h-r, r); ctx.lineTo(0, r); ctx.arcTo(0, 0, r, 0, r); ctx.closePath(); break; }
    case 'arrow-right': { const aw = w*0.38, sy = h*0.28; ctx.moveTo(0, sy); ctx.lineTo(w-aw, sy); ctx.lineTo(w-aw, 0); ctx.lineTo(w, cy); ctx.lineTo(w-aw, h); ctx.lineTo(w-aw, h-sy); ctx.lineTo(0, h-sy); ctx.closePath(); break; }
    case 'speech-bubble': { const r = Math.min(w,h)*0.1, bh = h*0.76, tx = w*0.12, tw = w*0.14; ctx.moveTo(r, 0); ctx.lineTo(w-r, 0); ctx.arcTo(w, 0, w, r, r); ctx.lineTo(w, bh-r); ctx.arcTo(w, bh, w-r, bh, r); ctx.lineTo(tx+tw, bh); ctx.lineTo(tx, h); ctx.lineTo(tx, bh); ctx.lineTo(r, bh); ctx.arcTo(0, bh, 0, bh-r, r); ctx.lineTo(0, r); ctx.arcTo(0, 0, r, 0, r); ctx.closePath(); break; }
    case 'shield': { const r = w*0.12; ctx.moveTo(r, 0); ctx.lineTo(w-r, 0); ctx.arcTo(w, 0, w, r, r); ctx.lineTo(w, h*0.52); ctx.bezierCurveTo(w, h*0.72, cx, h*0.88, cx, h); ctx.bezierCurveTo(cx, h*0.88, 0, h*0.72, 0, h*0.52); ctx.lineTo(0, r); ctx.arcTo(0, 0, r, 0, r); ctx.closePath(); break; }
    case 'chevron': { const notch = w*0.28; ctx.moveTo(0, 0); ctx.lineTo(w-notch, 0); ctx.lineTo(w, cy); ctx.lineTo(w-notch, h); ctx.lineTo(0, h); ctx.lineTo(notch, cy); ctx.closePath(); break; }
    case 'trapezoid': { const inset = w*0.15; ctx.moveTo(inset, 0); ctx.lineTo(w-inset, 0); ctx.lineTo(w, h); ctx.lineTo(0, h); ctx.closePath(); break; }
    default: ctx.rect(0, 0, w, h);
  }
};

function ImageElement({ el }) {
  const src = !el.src ? null
    : el.src.startsWith('http') || el.src.startsWith('data:') ? el.src
    : getMediaUrl(el.src);
  const [img] = useImage(src || '', 'anonymous');
  if (!src || !img) return null;

  if (el.maskShape) {
    return (
      <Group
        x={el.x ?? 0}
        y={el.y ?? 0}
        rotation={el.rotation || 0}
        opacity={el.opacity ?? 1}
        listening={false}
        clipFunc={(ctx) => drawMaskPath(ctx, el.maskShape, el.width, el.height)}
      >
        <KonvaImage image={img} x={0} y={0} width={el.width} height={el.height} />
      </Group>
    );
  }

  return (
    <KonvaImage
      image={img}
      x={el.x ?? 0}
      y={el.y ?? 0}
      width={el.width}
      height={el.height}
      rotation={el.rotation || 0}
      opacity={el.opacity ?? 1}
      listening={false}
    />
  );
}

const SlideCanvasPreview = ({ slideData, canvasWidth = 1080, canvasHeight = 1080, previewWidth = 500 }) => {
  const scale = previewWidth / canvasWidth;
  const previewHeight = Math.round(canvasHeight * scale);
  const elements = slideData?.elements || [];
  const bgColor = slideData?.backgroundColor || '#ffffff';

  if (!elements.length) {
    return (
      <div
        className="flex flex-col items-center justify-center bg-gray-100 rounded-lg text-gray-400"
        style={{ width: previewWidth, height: previewHeight }}
      >
        <p className="text-sm font-medium">Not edited yet</p>
        <p className="text-xs mt-1">Open in Editor to start editing</p>
      </div>
    );
  }

  const getShadowProps = (el) => ({
    shadowColor: el.shadowColor || 'rgba(0,0,0,0.5)',
    shadowBlur: el.shadowBlur || 0,
    shadowOffsetX: el.shadowOffsetX || 0,
    shadowOffsetY: el.shadowOffsetY || 0,
  });

  const renderElement = (el) => {
    const commonProps = {
      key: el.id,
      listening: false,
    };

    switch (el.type) {
      case 'image':
        return <ImageElement key={el.id} el={el} />;

      case 'text':
        return (
          <KonvaText
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            width={el.width}
            text={el.text || ''}
            fontSize={el.fontSize || 20}
            fontFamily={el.fontFamily || 'Arial'}
            fontStyle={el.fontStyle || 'normal'}
            textDecoration={el.textDecoration || ''}
            fill={el.fill || '#000000'}
            align={el.align || 'left'}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            lineHeight={el.lineHeight ?? 1}
            letterSpacing={el.letterSpacing ?? 0}
            {...getShadowProps(el)}
          />
        );

      case 'rect':
        return (
          <Rect
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            width={el.width}
            height={el.height}
            fill={el.fill || 'transparent'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            cornerRadius={el.cornerRadius || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            {...getShadowProps(el)}
          />
        );

      case 'circle':
        return (
          <Circle
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            radius={el.radius || 50}
            fill={el.fill || 'transparent'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            {...getShadowProps(el)}
          />
        );

      case 'ellipse':
        return (
          <Ellipse
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            radiusX={el.radiusX || 100}
            radiusY={el.radiusY || 60}
            fill={el.fill || 'transparent'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            {...getShadowProps(el)}
          />
        );

      case 'line-shape':
        return (
          <KonvaLine
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            points={[0, 0, el.length || 200, 0]}
            stroke={el.stroke || '#475569'}
            strokeWidth={el.strokeWidth || 4}
            dash={el.dash || []}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
          />
        );

      case 'arrow-shape':
        return (
          <Arrow
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            points={[0, 0, el.length || 200, 0]}
            pointerLength={el.pointerLength || 20}
            pointerWidth={el.pointerWidth || 16}
            pointerAtBeginning={el.pointerAtBeginning || false}
            stroke={el.stroke || '#475569'}
            strokeWidth={el.strokeWidth || 4}
            fill={el.fill || '#475569'}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
          />
        );

      case 'triangle':
        return (
          <RegularPolygon
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            sides={3}
            radius={el.radius || 90}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            {...getShadowProps(el)}
          />
        );

      case 'diamond':
        return (
          <RegularPolygon
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            sides={4}
            radius={el.radius || 90}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={(el.rotation || 0) + 45}
            opacity={el.opacity ?? 1}
            {...getShadowProps(el)}
          />
        );

      case 'pentagon':
        return (
          <RegularPolygon
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            sides={5}
            radius={el.radius || 90}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            {...getShadowProps(el)}
          />
        );

      case 'hexagon':
        return (
          <RegularPolygon
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            sides={6}
            radius={el.radius || 90}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            {...getShadowProps(el)}
          />
        );

      case 'octagon':
        return (
          <RegularPolygon
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            sides={8}
            radius={el.radius || 90}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            {...getShadowProps(el)}
          />
        );

      case 'star':
        return (
          <Star
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            numPoints={el.numPoints || 5}
            innerRadius={el.innerRadius || 36}
            outerRadius={el.outerRadius || 90}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            {...getShadowProps(el)}
          />
        );

      case 'ring':
        return (
          <Ring
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            innerRadius={el.innerRadius || 40}
            outerRadius={el.outerRadius || 80}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            {...getShadowProps(el)}
          />
        );

      case 'cross': {
        const cw = el.width || 160;
        const ch = el.height || 160;
        const arm = cw * 0.28;
        return (
          <Shape
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            width={cw}
            height={ch}
            {...getShadowProps(el)}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.moveTo(arm, 0);
              ctx.lineTo(cw - arm, 0);
              ctx.lineTo(cw - arm, arm);
              ctx.lineTo(cw, arm);
              ctx.lineTo(cw, ch - arm);
              ctx.lineTo(cw - arm, ch - arm);
              ctx.lineTo(cw - arm, ch);
              ctx.lineTo(arm, ch);
              ctx.lineTo(arm, ch - arm);
              ctx.lineTo(0, ch - arm);
              ctx.lineTo(0, arm);
              ctx.lineTo(arm, arm);
              ctx.closePath();
              ctx.fillStrokeShape(shape);
            }}
          />
        );
      }

      case 'heart': {
        const hw = el.width || 160;
        const hh = el.height || 150;
        return (
          <Shape
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            width={hw}
            height={hh}
            {...getShadowProps(el)}
            sceneFunc={(ctx, shape) => {
              const w = hw, h = hh;
              ctx.beginPath();
              ctx.moveTo(w / 2, h * 0.4);
              ctx.bezierCurveTo(w * 0.48, h * 0.08, 0, h * 0.08, 0, h * 0.4);
              ctx.bezierCurveTo(0, h * 0.68, w / 2, h * 0.88, w / 2, h);
              ctx.bezierCurveTo(w, h * 0.88, w, h * 0.68, w, h * 0.4);
              ctx.bezierCurveTo(w, h * 0.08, w * 0.52, h * 0.08, w / 2, h * 0.4);
              ctx.closePath();
              ctx.fillStrokeShape(shape);
            }}
          />
        );
      }

      case 'cloud': {
        const clw = el.width || 220;
        const clh = el.height || 140;
        return (
          <Shape
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            width={clw}
            height={clh}
            {...getShadowProps(el)}
            sceneFunc={(ctx, shape) => {
              const w = clw, h = clh;
              ctx.beginPath();
              ctx.arc(w * 0.22, h * 0.58, h * 0.32, Math.PI * 0.5, Math.PI * 1.5, false);
              ctx.arc(w * 0.45, h * 0.3, h * 0.26, Math.PI * 1.1, Math.PI * 1.95, false);
              ctx.arc(w * 0.7, h * 0.25, h * 0.22, Math.PI * 1.6, Math.PI * 1.98, false);
              ctx.arc(w * 0.8, h * 0.55, h * 0.28, Math.PI * 1.55, Math.PI * 0.5, false);
              ctx.closePath();
              ctx.fillStrokeShape(shape);
            }}
          />
        );
      }

      case 'wavy-line': {
        const wlen = el.length || 200, wamp = el.amplitude || 15, wfreq = el.frequency || 4;
        const wpts = [];
        for (let i = 0; i <= 60; i++) {
          wpts.push((i / 60) * wlen);
          wpts.push(Math.sin((i / 60) * Math.PI * 2 * wfreq) * wamp);
        }
        return (
          <KonvaLine
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            points={wpts}
            tension={0.4}
            stroke={el.stroke || '#475569'}
            strokeWidth={el.strokeWidth || 4}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
          />
        );
      }

      case 'curved-arrow': {
        const caw2 = el.width || 200, cah2 = el.height || 120;
        const col2 = el.stroke || '#475569', sw2 = el.strokeWidth || 4;
        const x1 = 0, y1 = cah2 * 0.72;
        const x2 = caw2, y2 = cah2 * 0.72;
        const cpx = caw2 / 2, cpy = 0;
        const angle = Math.atan2(y2 - cpy, x2 - cpx), as = sw2 * 4;
        return (
          <Shape
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            fill={col2}
            stroke={col2}
            strokeWidth={sw2}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            width={caw2}
            height={cah2}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.moveTo(x1, y1);
              ctx.quadraticCurveTo(cpx, cpy, x2, y2);
              ctx.strokeShape(shape);
              ctx.beginPath();
              ctx.moveTo(x2, y2);
              ctx.lineTo(x2 - as * Math.cos(angle - 0.45), y2 - as * Math.sin(angle - 0.45));
              ctx.lineTo(x2 - as * Math.cos(angle + 0.45), y2 - as * Math.sin(angle + 0.45));
              ctx.closePath();
              ctx.fillShape(shape);
            }}
          />
        );
      }

      case 'parallelogram': {
        const pw = el.width || 200, ph = el.height || 120, sk = pw * 0.2;
        return (
          <Shape
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            width={pw}
            height={ph}
            {...getShadowProps(el)}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.moveTo(sk, 0); ctx.lineTo(pw, 0);
              ctx.lineTo(pw - sk, ph); ctx.lineTo(0, ph);
              ctx.closePath(); ctx.fillStrokeShape(shape);
            }}
          />
        );
      }

      case 'trapezoid': {
        const tw2 = el.width || 200, th2 = el.height || 120, ins = tw2 * 0.15;
        return (
          <Shape
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            width={tw2}
            height={th2}
            {...getShadowProps(el)}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.moveTo(ins, 0); ctx.lineTo(tw2 - ins, 0);
              ctx.lineTo(tw2, th2); ctx.lineTo(0, th2);
              ctx.closePath(); ctx.fillStrokeShape(shape);
            }}
          />
        );
      }

      case 'chevron': {
        const chw = el.width || 200, chh = el.height || 120, cn = chw * 0.28;
        return (
          <Shape
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            width={chw}
            height={chh}
            {...getShadowProps(el)}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.moveTo(0, 0); ctx.lineTo(chw - cn, 0);
              ctx.lineTo(chw, chh / 2); ctx.lineTo(chw - cn, chh);
              ctx.lineTo(0, chh); ctx.lineTo(cn, chh / 2);
              ctx.closePath(); ctx.fillStrokeShape(shape);
            }}
          />
        );
      }

      case 'shield': {
        const shw = el.width || 160, shh = el.height || 180, sr = shw * 0.12;
        return (
          <Shape
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            width={shw}
            height={shh}
            {...getShadowProps(el)}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.moveTo(sr, 0); ctx.lineTo(shw - sr, 0);
              ctx.arcTo(shw, 0, shw, sr, sr);
              ctx.lineTo(shw, shh / 2);
              ctx.bezierCurveTo(shw, shh * 0.75, shw / 2, shh * 0.925, shw / 2, shh);
              ctx.bezierCurveTo(shw / 2, shh * 0.925, 0, shh * 0.75, 0, shh / 2);
              ctx.lineTo(0, sr);
              ctx.arcTo(0, 0, sr, 0, sr);
              ctx.closePath(); ctx.fillStrokeShape(shape);
            }}
          />
        );
      }

      case 'callout': {
        const cltw = el.width || 200, clth = el.height || 160;
        const clr = Math.min(cltw, clth) * 0.1;
        return (
          <Shape
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            width={cltw}
            height={clth}
            {...getShadowProps(el)}
            sceneFunc={(ctx, shape) => {
              const bot = clth * 0.76;
              const tailx = cltw * 0.12, tailw = cltw * 0.14;
              ctx.beginPath();
              ctx.moveTo(clr, 0); ctx.lineTo(cltw - clr, 0);
              ctx.arcTo(cltw, 0, cltw, clr, clr);
              ctx.lineTo(cltw, bot - clr);
              ctx.arcTo(cltw, bot, cltw - clr, bot, clr);
              ctx.lineTo(tailx + tailw, bot);
              ctx.lineTo(tailx, clth); ctx.lineTo(tailx, bot);
              ctx.lineTo(clr, bot);
              ctx.arcTo(0, bot, 0, bot - clr, clr);
              ctx.lineTo(0, clr);
              ctx.arcTo(0, 0, clr, 0, clr);
              ctx.closePath(); ctx.fillStrokeShape(shape);
            }}
          />
        );
      }

      case 'crescent': {
        const cr2 = el.radius || 80;
        return (
          <Shape
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            width={cr2 * 2}
            height={cr2 * 2}
            offsetX={cr2}
            offsetY={cr2}
            {...getShadowProps(el)}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.arc(cr2 * 0.9, cr2, cr2, -Math.PI * 0.65, Math.PI * 0.65, false);
              ctx.arc(cr2 * 1.2, cr2, cr2 * 0.85, Math.PI * 0.65, -Math.PI * 0.65, true);
              ctx.closePath(); ctx.fillStrokeShape(shape);
            }}
          />
        );
      }

      case 'ribbon': {
        const rbw = el.width || 200, rbh = el.height || 80, rbn = rbh * 0.35;
        return (
          <Shape
            {...commonProps}
            x={el.x ?? 0}
            y={el.y ?? 0}
            fill={el.fill || '#94a3b8'}
            stroke={el.stroke || ''}
            strokeWidth={el.strokeWidth || 0}
            rotation={el.rotation || 0}
            opacity={el.opacity ?? 1}
            width={rbw}
            height={rbh}
            {...getShadowProps(el)}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.moveTo(0, 0); ctx.lineTo(rbw, 0);
              ctx.lineTo(rbw - rbn, rbh / 2); ctx.lineTo(rbw, rbh);
              ctx.lineTo(0, rbh); ctx.lineTo(rbn, rbh / 2);
              ctx.closePath(); ctx.fillStrokeShape(shape);
            }}
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <Stage width={previewWidth} height={previewHeight}>
      <Layer scaleX={scale} scaleY={scale}>
        <Rect
          x={0} y={0}
          width={canvasWidth} height={canvasHeight}
          fill={bgColor}
          listening={false}
        />
        {elements.map(renderElement)}
      </Layer>
    </Stage>
  );
};

export default SlideCanvasPreview;
